import React, { useEffect } from 'react';
import Layout from '../layout/Layout';
import { usePageContent } from '../../context/ContentContext';

const MachineCard = ({ title, body }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="font-poppins font-semibold text-h3 mb-3 text-primary-red">
            {title}
        </h3>
        <p className="font-poppins font-normal text-sub text-gray-700">
            {body}
        </p>
    </div>
);

const MadeInLondon = () => {
    const { t, g } = usePageContent('madeInLondon');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Layout>
            {/* Hero Section */}
            <section className="relative h-[70vh] overflow-hidden">
                <img
                    src={t('hero.image')}
                    alt={t('hero.title')}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Lighter overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40 flex items-center justify-center">
                    <h1 className="font-poppins font-light text-h1 text-white text-center px-4">
                        {t('hero.title')}
                    </h1>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto font-poppins font-normal text-body text-center">
                        {t('intro.paragraphs').map((paragraph, index) => (
                            <p key={index} className="mb-6">{paragraph}</p>
                        ))}
                        <div className="w-24 h-1 bg-primary-red mx-auto mb-8"></div>
                    </div>
                </div>
            </section>

            {/* Production Capabilities */}
            <section className="py-16 bg-background-cool-blue">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 text-center mb-12 text-primary-dark-red">
                        {t('capabilities.title')}
                    </h2>

                    <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto mb-12">
                        {t('capabilities.stats').map((stat, index) => (
                            <div key={index} className="text-center bg-white p-6 rounded-lg shadow-sm">
                                <div className="text-4xl font-light text-primary-red mb-4">{stat.value}</div>
                                <h3 className="font-poppins font-semibold text-h3 mb-2 text-primary-dark-red">{stat.title}</h3>
                                <p className="font-poppins font-normal text-sub text-gray-700">
                                    {stat.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Production Process */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 text-center mb-12 text-primary-dark-red">
                        {t('process.title')}
                    </h2>

                    <div className="max-w-6xl mx-auto">
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {t('process.steps').map((step, index) => (
                                <div key={index} className="text-center bg-accent-powder-green p-6 rounded-lg">
                                    <div className="flex justify-center mb-4 h-24">
                                        <img src={step.image} alt={step.title} className="h-full w-auto object-contain" />
                                    </div>
                                    <h3 className="font-poppins font-semibold text-h3 mb-3 text-primary-red">
                                        {step.title}
                                    </h3>
                                    <p className="font-poppins font-normal text-sub text-gray-700">
                                        {step.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Machinery & Equipment */}
            <section className="py-20 bg-accent-powder-green">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 text-center mb-12 text-primary-dark-red">
                        {t('machinery.title')}
                    </h2>

                    <div className="max-w-6xl mx-auto">
                        {/* Flat Bed Knitting Machines */}
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                            {t('machinery.knitting').map((machine, index) => (
                                <MachineCard key={index} title={machine.title} body={machine.body} />
                            ))}
                        </div>

                        {/* Linking Machines */}
                        <h3 className="font-poppins font-semibold text-h3 mb-6 text-center text-primary-dark-red mt-10">
                            {t('machinery.linkingTitle')}
                        </h3>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                            {t('machinery.linking').map((machine, index) => (
                                <MachineCard key={index} title={machine.title} body={machine.body} />
                            ))}
                        </div>

                        {/* Equipment & Software */}
                        <h3 className="font-poppins font-semibold text-h3 mb-6 text-center text-primary-dark-red mt-10">
                            {t('machinery.equipmentTitle')}
                        </h3>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {t('machinery.equipment').map((item, index) => (
                                <MachineCard key={index} title={item.title} body={item.body} />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Location */}
            <section className="py-20 bg-primary-dark-red text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="font-poppins font-light text-h2 mb-6">
                        {t('location.title')}
                    </h2>
                    <p className="font-poppins font-normal text-body max-w-2xl mx-auto mb-8">
                        {t('location.address').map((line, index) => (
                            <React.Fragment key={index}>
                                {line}
                                <br />
                            </React.Fragment>
                        ))}
                    </p>
                    <p className="font-poppins font-normal text-body mb-10">
                        {t('location.body')}
                    </p>
                    <a
                        href={`mailto:${g('contact.email')}`}
                        className="inline-block px-8 py-4 bg-primary-red text-white hover:bg-pink-600 transition-colors duration-300 font-poppins font-semibold text-h3 rounded-lg"
                    >
                        {t('location.buttonText')}
                    </a>
                </div>
            </section>
        </Layout>
    );
};

export default MadeInLondon;
